import { useRef, useEffect } from "react";
import cn from "classnames";
import { usePostHog } from "posthog-js/react";
import { X, Pencil } from "lucide-react";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { formatNumber, toFixed2 } from "utils/formators";
import { DistributionLocation } from "module/eddm/dto";
import { getRouteResidences } from "utils/eddm";
import { Grow } from "@material-ui/core";
import { useStore } from "store";
import { DMDocumentNames } from "shared/constants";

interface EDDMSummaryProps {
  grow: boolean;
  setGrow: (grow: boolean) => void;
  isNextActive: boolean;
  onContinue: () => void;
  setFlyerSelectModalOpen: (value: boolean) => void;
}

export const EDDMSummary = ({ grow, setGrow, isNextActive, onContinue, setFlyerSelectModalOpen }: EDDMSummaryProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    user,
    campaign: { flyerType, id: campaignId },
    client: { name: clientName },
    distributionLocations,
    costsCalculationData: { costPerFlyer, detailedCost, flyersAmount },
  } = useStore();
  const { subtotal } = detailedCost || {};
  const posthog = usePostHog();
  const postHogTracker = new EmitAnalyticsEvent(posthog);

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setGrow(false);
    }
  };

  useEffect(() => {
    if (grow) {
      postHogTracker.run({
        eventName: "Clicked to see cost summary",
        userId: user?.id,
        clientName,
        device: navigator.userAgent,
        additionalPayload: {
          campaignId,
        },
      });
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [grow]);

  return (
    <Grow in={grow} {...(grow ? { timeout: 1000 } : {})} style={{ transformOrigin: "top right" }}>
      <div
        ref={containerRef}
        className="absolute right-0 top-[75px] z-[9999] box-border flex max-h-[98vh] min-h-[281px] w-[360px] flex-col justify-between rounded-lg border border-solid border-[#F2F2F6] bg-popover px-8 pb-2 pt-4 dark:border-border"
      >
        <div className="flex w-full flex-col">
          <div className="flex w-full items-center justify-between">
            <span className="text-lg font-bold text-[#0C172C] dark:text-foreground">Summary</span>
            <X className="h-5 w-5 cursor-pointer text-foreground" onClick={() => setGrow(false)} />
          </div>
        </div>
        <div className="mt-3 flex w-full items-center justify-between">
          <span className="text-sm font-normal text-[#707087] dark:text-muted-foreground">Postcard format </span>
          <button
            data-cy="eddmSummaryChangeFormatButton"
            className="relative flex cursor-pointer items-center justify-between overflow-hidden px-3 py-1 text-sm font-semibold text-[#2E3860] before:absolute before:inset-0 before:z-0 before:translate-x-full before:bg-[#E2E2FC] before:transition-transform before:duration-[400ms] before:ease-out before:content-[''] hover:before:translate-x-0 dark:text-foreground dark:before:bg-accent [&>span]:relative [&>span]:z-[1] [&>span]:ml-2.5 [&>svg]:relative [&>svg]:z-[1] [&>svg]:ml-2.5"
            onClick={() => setFlyerSelectModalOpen(true)}
          >
            <span>{DMDocumentNames[flyerType as keyof typeof DMDocumentNames]}</span>{" "}
            <Pencil className="h-[17px] w-[17px]" />
          </button>
        </div>

        <div className="mt-3 flex w-full flex-col">
          <div className="flex w-full items-center justify-start">
            <span className="text-lg font-bold text-[#0C172C] dark:text-foreground">Segments</span>
          </div>
          <div className="max-h-[70vh] w-[103%] overflow-y-scroll overflow-x-hidden">
            {distributionLocations?.map((distributionLocation: DistributionLocation) => {
              const totalAddresses = getRouteResidences(distributionLocation.selectedRoutes);
              return (
                <div className="mt-3 flex w-full items-center justify-between" key={distributionLocation.name}>
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium text-[#2E3860] dark:text-foreground">
                      {distributionLocation.name}
                    </span>
                    <span className="text-sm text-[#707087] dark:text-muted-foreground">
                      {distributionLocation.selectedRoutes.length} routes
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-semibold text-[#2E3860] dark:text-foreground">
                      {" "}
                      $ {formatNumber(toFixed2(costPerFlyer * totalAddresses))}
                    </span>
                    <span className="flex items-center text-sm text-[#707087] dark:text-muted-foreground">
                      {totalAddresses} residences
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex w-full items-center justify-between">
          <span className="text-lg font-bold text-[#0C172C] dark:text-foreground">Total</span>
          <div className="flex flex-col items-end">
            <span className="text-lg font-bold text-[#0C172C] dark:text-foreground">$ {formatNumber(subtotal)}</span>
            <span className="text-sm text-[#707087] dark:text-muted-foreground">
              {formatNumber(flyersAmount) || "Set"} {flyersAmount ? "R" : "r"}esidences
            </span>
          </div>
        </div>

        <button
          className={cn(
            "mt-[5px] flex h-[45px] w-full items-center justify-center rounded-lg text-center text-base font-bold",
            isNextActive
              ? "cursor-pointer bg-primary text-primary-foreground transition-opacity hover:opacity-90"
              : "cursor-default bg-[#E4E4EA] text-[#C2C5DD] dark:bg-muted dark:text-muted-foreground"
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onContinue();
          }}
          disabled={!isNextActive}
        >
          Continue
        </button>
      </div>
    </Grow>
  );
};
